import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import constants from '../constants';
import helpers from '../helpers';
import CookiebotIntegrationContainer from '../../integrations/containers/cookiebot-integration-container';
import { useWebsiteSettingsContext } from '../context/website-settings-context';

const { InputGroupField, CheckboxField } = designSystem;
const { Controller } = reactHookForm;

const {
  Stack,
  ImagePickerPreview,
  Paragraph,
  Heading,
  HeadingGroup,
  ControlGroup,
  Box,
  ActionButton,
} = designSystem;
const { tabIdentifiers, settingTypes } = constants;

function GeneralSection({ siteNameRef }) {
  const {
    control,
    captions,
    onWrongTypeFile,
    uploadImage,
    onUnpublish = null,
    onResetSite = null,
    onSwitchTemplate = null,
    isSiteExportEnabled = false,
    onExportSite,
    setImagePreview,
    integrationTypes,
    formValues: form,
    initialValues,
    showPromptForce = false,
    isBusinessNameSameAsSiteName,
    setFieldValue,
    onSiteNameDescriptionClick,
  } = useWebsiteSettingsContext();

  const onExportSiteCallback = isSiteExportEnabled ? onExportSite : null;
  const { favicon } = form;

  const [faviconPreview, setFaviconPreview] = useState(setImagePreview(favicon));

  const preventGoogleSearchIndexingSection = (
    <ControlGroup
      title={captions.dangerZoneCaptions.preventGoogleSearchIndexing.name}
      description={captions.dangerZoneCaptions.preventGoogleSearchIndexing.description}
    >
      <CheckboxField
        control={control}
        id="googleSearch"
        name={settingTypes.PREVENT_INDEXING}
        label={captions.dangerZoneCaptions.preventGoogleSearchIndexing.label}
      />
    </ControlGroup>
  );

  const unpublishSection = (
    <ControlGroup
      title={captions.dangerZoneCaptions.unpublish.name}
      description={captions.dangerZoneCaptions.unpublish.description}
    >
      <ActionButton
        onClick={onUnpublish}
        appearance="danger"
        label={captions.dangerZoneCaptions.unpublish.button}
      />
    </ControlGroup>
  );

  const resetSiteSection = (
    <ControlGroup
      title={captions.dangerZoneCaptions.reset.name}
      description={captions.dangerZoneCaptions.reset.description}
    >
      <ActionButton
        onClick={onResetSite}
        appearance="danger"
        label={captions.dangerZoneCaptions.reset.button}
      />
    </ControlGroup>
  );

  const switchTemplateSection = (
    <ControlGroup
      title={captions.dangerZoneCaptions.switchTemplate.name}
      description={captions.dangerZoneCaptions.switchTemplate.description}
    >
      <ActionButton
        onClick={onSwitchTemplate}
        appearance="danger"
        label={captions.dangerZoneCaptions.switchTemplate.button}
      />
    </ControlGroup>
  );

  const dangerSection = (
    <HeadingGroup
      title={captions.dangerZoneCaptions.name}
      description={captions.dangerZoneCaptions.description}
      appearance="danger"
    >
      <Box marginTop="spacing-m">
        <Stack gap="spacing-m">
          {preventGoogleSearchIndexingSection}
          {onUnpublish && unpublishSection}
          {onSwitchTemplate && switchTemplateSection}
          {onResetSite && resetSiteSection}
        </Stack>
      </Box>
    </HeadingGroup>
  );

  const onSiteNameChange = (value) => {
    if (isBusinessNameSameAsSiteName) {
      setFieldValue(settingTypes.BUSINESS_NAME, value);
    }
  };

  return (
    <Stack gap="spacing-m">
      {/* eslint-disable-next-line yola/react/no-unknown-property */}
      <div settings-field-name={settingTypes.SITE_NAME} ref={siteNameRef}>
        <Stack gap="spacing-3-xs">
          <Heading type="heading-6">{captions.siteName.name}</Heading>

          <div>
            <InputGroupField
              control={control}
              rules={{
                required: captions.siteName.errorMessages.required,
              }}
              name={settingTypes.SITE_NAME}
              onChange={onSiteNameChange}
            />
          </div>

          <Paragraph size="small" appearance="medium-emphasis">
            <span
              onClick={onSiteNameDescriptionClick}
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{ __html: captions.siteName.description }}
            />
          </Paragraph>
        </Stack>
      </div>

      {/* eslint-disable-next-line yola/react/no-unknown-property */}
      <div settings-field-name={settingTypes.FAVICON}>
        <Stack gap="spacing-3-xs">
          <Heading type="heading-6">{captions.favicon.name}</Heading>
          <Controller
            name={settingTypes.FAVICON}
            control={control}
            render={({ field }) => (
              <ImagePickerPreview
                imageSource={faviconPreview}
                accept=".ico, .png"
                label={captions.fileInputCaptions.button}
                placeholder={captions.fileInputCaptions.empty}
                fileItemName={helpers.getFileName(faviconPreview)}
                statusText={captions.favicon.description}
                onChange={(filesList) => {
                  uploadImage(filesList, form, settingTypes.FAVICON);
                  field.onChange(filesList[0].name);
                }}
                onClear={() => {
                  setFaviconPreview(null);
                  field.onChange('');
                }}
                onError={(wrongExtList) =>
                  onWrongTypeFile(wrongExtList, form, settingTypes.FAVICON)
                }
              />
            )}
          />
        </Stack>
      </div>

      <HeadingGroup title={captions.cookiesUserData.name}>
        <Stack gap="spacing-xs">
          {!form[integrationTypes.COOKIEBOT_ID] && (
            <CheckboxField
              control={control}
              id="cookiesUserData"
              name={settingTypes.IS_COOKIE_BANNER_ENABLED}
              label={captions.cookiesUserData.title}
              description={captions.cookiesUserData.description}
            />
          )}
          <CookiebotIntegrationContainer
            form={form}
            activeTabId={tabIdentifiers.GENERAL}
            captions={captions.integrations.cookiebot}
            initialValues={initialValues}
            showPromptForce={showPromptForce}
          />
        </Stack>
      </HeadingGroup>

      {(onUnpublish || onResetSite || onSwitchTemplate) && dangerSection}

      {onExportSiteCallback && (
        <ControlGroup
          title={captions.exportSite.name}
          description={captions.exportSite.description}
        >
          <ActionButton onClick={onExportSiteCallback} label={captions.exportSite.button} />
        </ControlGroup>
      )}
    </Stack>
  );
}

GeneralSection.propTypes = {
  siteNameRef: PropTypes.shape().isRequired,
};

export default GeneralSection;
